import { UnleashClient } from "unleash-proxy-client";
import { fromEvent, timer, merge, firstValueFrom, connectable, ReplaySubject } from "rxjs";
import { distinctUntilChanged, map, shareReplay, tap } from "rxjs/operators";
export class UnleashFeatureFlagWebClient {
    /**
     * Constructs a new UnleashFeatureFlagWebClient.
     *
     * @param configuration the standard
     * [Unleash configuration options](https://docs.getunleash.io/reference/sdks/javascript-browser#available-options)
     * with the addition of the custom Exact Sciences readyTimeout
     */
    constructor(configuration) {
        this._flagObservablesAfterReadyOrTimeout = {};
        this._flagObservablesAfterInitialized = {};
        this._variantObservablesAfterInitialized = {};
        this.unleash = new UnleashClient(configuration);
        const initializedConnectable = connectable(fromEvent(this.unleash, "initialized").pipe(tap(() => console.debug("unleash has read local cached data in the storageProvider"))), {
            connector: () => new ReplaySubject(1),
            resetOnDisconnect: false,
        });
        // allows future subscribers to replay initialized events that occur before subscription
        // see https://rxjs.dev/deprecations/multicasting#publishreplay
        initializedConnectable.connect();
        this._initialized$ = initializedConnectable;
        const optionalTimeout = configuration === null || configuration === void 0 ? void 0 : configuration.readyTimeout;
        const timeout = optionalTimeout != undefined ? optionalTimeout : 3000;
        const readyTimeoutConnectable = connectable(timer(timeout).pipe(tap(() => console.debug("Unleash ready timeout reached"))), {
            connector: () => new ReplaySubject(1),
            resetOnDisconnect: false,
        });
        // allows future subscribers to replay initialized events that occur before subscription
        // see https://rxjs.dev/deprecations/multicasting#publishreplay
        readyTimeoutConnectable.connect();
        this._readyTimeout$ = readyTimeoutConnectable;
        const updateConnectable = connectable(fromEvent(this.unleash, "update").pipe(tap(() => console.debug("Unleash Proxy returned a new feature toggle configuration"))), {
            connector: () => new ReplaySubject(1),
            resetOnDisconnect: false,
        });
        // allows future subscribers to replay initialized events that occur before subscription
        // see https://rxjs.dev/deprecations/multicasting#publishreplay
        updateConnectable.connect();
        this._update$ = updateConnectable;
        this.unleash.start();
    }
    isEnabled(feature) {
        return this.unleash.isEnabled(feature);
    }
    isEnabledAfterReady$(name) {
        if (Object.prototype.hasOwnProperty.call(this._flagObservablesAfterReadyOrTimeout, name)) {
            return this._flagObservablesAfterReadyOrTimeout[name];
        }
        const newObservable = merge(
        // publish on unleash client updates
        this._update$, 
        // publish when the ready timeout expires
        this._readyTimeout$).pipe(map(() => this.unleash.isEnabled(name)), 
        // avoid re-publishing unchanged statuses
        distinctUntilChanged(), tap((isEnabled) => console.log(`flag ${name} changed to ${isEnabled}`)), 
        // multicast and remember the last published feature flag status for new subscribers
        shareReplay(1));
        this._flagObservablesAfterReadyOrTimeout[name] = newObservable;
        return newObservable;
    }
    isEnabledAfterReady(name) {
        return firstValueFrom(this.isEnabledAfterReady$(name));
    }
    isEnabledAfterInitialized$(name) {
        if (Object.prototype.hasOwnProperty.call(this._flagObservablesAfterInitialized, name)) {
            return this._flagObservablesAfterInitialized[name];
        }
        const newObservable = merge(
        // publish on unleash client updates
        this._update$, 
        // publish when the unleash client initializes (including defaults)
        this._initialized$).pipe(map(() => this.unleash.isEnabled(name)), 
        // avoid re-publishing unchanged statuses
        distinctUntilChanged(), tap((isEnabled) => console.log(`flag ${name} changed to ${isEnabled}`)), 
        // multicast and remember the last published feature flag status for new subscribers
        shareReplay(1));
        this._flagObservablesAfterInitialized[name] = newObservable;
        return newObservable;
    }
    isEnabledAfterInitialized(name) {
        return firstValueFrom(this.isEnabledAfterInitialized$(name));
    }
    setUserId(userId) {
        this.unleash.setContextField("userId", userId);
    }
    setUserIdAndWaitForUpdate(userId) {
        return this.unleash.updateContext(Object.assign(Object.assign({}, this.unleash.getContext()), { userId: userId }));
    }
    onImpressionHook(callback) {
        this.unleash.on('impression', callback);
    }
    getVariantAfterInitialized$(name) {
        if (Object.prototype.hasOwnProperty.call(this._variantObservablesAfterInitialized, name)) {
            return this._variantObservablesAfterInitialized[name];
        }
        const newObservable = merge(
        // publish on unleash client updates
        this._update$, 
        // publish when the unleash client initializes (including defaults)
        this._initialized$).pipe(map(() => this.unleash.getVariant(name)), 
        // avoid re-publishing unchanged statuses
        distinctUntilChanged(), tap((variant) => console.log(`variant of flag ${name} is ${JSON.stringify(variant)}`)), 
        // multicast and remember the last published variant status for new subscribers
        shareReplay(1));
        this._variantObservablesAfterInitialized[name] = newObservable;
        return newObservable;
    }
}
//# sourceMappingURL=unleash-feature-flag-web-client.js.map